<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <div class="border-b bg-white">
      <div class="max-w-4xl mx-auto px-6 py-4">
        <NuxtLink to="/" class="text-blue-600 hover:text-blue-700 font-medium">
          ← Back to Home
        </NuxtLink>
      </div>
    </div>

    <!-- Content -->
    <main class="max-w-4xl mx-auto px-6 py-12">
      <h1 class="text-3xl md:text-4xl font-extrabold text-slate-900 mb-8">
        Refund Policy
      </h1>

      <div class="prose prose-slate max-w-none">
        <p class="text-lg text-slate-600 mb-8">
          This Refund Policy outlines the conditions under which refunds may be granted for digital software licenses purchased from GVGmall. Please read this policy carefully before making a purchase.
        </p>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Digital Products Notice</h2>
          <p class="text-slate-600">
            All products sold by GVGmall are digital software licenses delivered via email. Due to the nature of digital products, refunds are handled on a case-by-case basis and depend on the delivery status and activation state of the license.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">When Refunds May Be Granted</h2>
          <p class="text-slate-600 mb-4">
            We may consider a refund request in the following situations:
          </p>
          <ul class="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>Delivery failure:</strong> The license key was not delivered to your email after order confirmation and our support team was unable to resolve the issue</li>
            <li><strong>Invalid license:</strong> The license key provided is invalid or non-functional, and our support team cannot provide a working replacement</li>
            <li><strong>Duplicate charge:</strong> You were accidentally charged multiple times for the same order</li>
            <li><strong>Technical failure:</strong> The license cannot be activated due to a verified technical issue that cannot be resolved by our support team</li>
          </ul>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">When Refunds Are Not Available</h2>
          <p class="text-slate-600 mb-4">
            Refund requests will typically not be accepted in the following cases:
          </p>
          <ul class="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>License activated:</strong> The license key has already been activated or used</li>
            <li><strong>Change of mind:</strong> You no longer want the product after successful delivery</li>
            <li><strong>Compatibility issues:</strong> The product is incompatible with your system, and this was not reported before attempting activation</li>
            <li><strong>Violation of terms:</strong> The license was used in violation of the software vendor's terms of service</li>
          </ul>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">How to Request a Refund</h2>
          <p class="text-slate-600 mb-4">
            To request a refund, please contact our support team with the following information:
          </p>
          <ul class="list-disc pl-6 text-slate-600 space-y-2">
            <li>Your order number or order reference</li>
            <li>The email address used for the purchase</li>
            <li>Product name and license key (if received)</li>
            <li>Detailed reason for the refund request</li>
            <li>Description of any support steps already attempted</li>
          </ul>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Refund Processing</h2>
          <p class="text-slate-600">
            Refund requests are typically reviewed within 2 business days. If approved, refunds will be processed to the original payment method used for the purchase. Processing time may vary depending on your payment provider and can take 5-10 business days to appear on your statement.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">After Activation</h2>
          <p class="text-slate-600">
            Once a software license has been successfully activated, refunds are only available in cases of proven technical failure that cannot be resolved by our support team. We encourage customers to verify their purchase carefully before activating any license.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Contact for Refund Requests</h2>
          <p class="text-slate-600 mb-4">
            For all refund inquiries and requests:
          </p>
          <p class="text-slate-900">
            <strong>Email:</strong> <a href="mailto:support@gvgmall.co" class="text-blue-600 hover:text-blue-700">support@gvgmall.co</a>
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Policy Updates</h2>
          <p class="text-slate-600">
            This refund policy may be updated periodically. The version in effect at the time of your purchase will apply to your order. Please review this page regularly for any changes.
          </p>
        </section>

        <p class="text-sm text-slate-500 mt-12">
          Last updated: June 2025
        </p>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'institutional'
})

useSeoMeta({
  title: 'Refund Policy | GVGmall',
  description: 'GVGmall Refund Policy - Learn about our refund conditions for digital software licenses.',
  ogTitle: 'Refund Policy | GVGmall',
  ogDescription: 'GVGmall Refund Policy - Learn about our refund conditions for digital products.',
  robots: 'index,follow'
})

useHead({
  link: [
    { rel: 'canonical', href: 'https://www.gvgmall.co/legal/refund-policy' }
  ]
})
</script>
